import React, { useMemo } from "react";
import { Menu, MenuProps } from "antd";
import { useLocation } from "react-router-dom";
import type { TFunction } from "i18next";
import { ROUTES_MAP } from "../../routes/routesMap";
import { IUser } from "../../types/models";
import { IconBase } from "../../assets/Icons/IconBase";
import { SidebarLiveIcon } from "../../assets/Icons/SidebarLive";
import { AdminIcon } from "../../assets/Icons/AdminIcon";
import { SidebarLogoutIcon } from "../../assets/Icons/SidebarLogout";
import { SidebarFAQIcon } from "../../assets/Icons/SidebarFAQ";

type MenuItem = Required<MenuProps>["items"][number];

export interface SidebarMenuItem {
  title: React.ReactNode;
  key: string;
  icon?: React.ReactNode;
}

export function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: "group"
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    type,
  } as MenuItem;
}

// Icons paint with currentColor, so they follow the Menu tokens: muted at rest,
// text on hover, on-color on the butter selected item.
export const buildMenuItems = (
  t: TFunction<"dashboard">,
  user?: IUser
): SidebarMenuItem[] => [
  ...(user?.role === "admin"
    ? [
        {
          title: t("nav.admin"),
          key: ROUTES_MAP.dashboard.admin.liveStreams,
          icon: (
            <IconBase>
              <AdminIcon />
            </IconBase>
          ),
        },
      ]
    : []),
  {
    title: t("nav.liveStreams"),
    key: ROUTES_MAP.dashboard.liveStreams,
    icon: (
      <IconBase>
        <SidebarLiveIcon />
      </IconBase>
    ),
  },
  {
    title: t("nav.faqs"),
    icon: (
      <IconBase>
        <SidebarFAQIcon />
      </IconBase>
    ),
    key: ROUTES_MAP.dashboard.faqs,
  },
  {
    title: <Menu.Divider />,
    key: "divider-2",
  },
  {
    title: t("nav.logout"),
    key: ROUTES_MAP.dashboard.logout,
    icon: (
      <IconBase>
        <SidebarLogoutIcon />
      </IconBase>
    ),
  },
];

/**
 * The menu keys to highlight for the router's current path, shared by the
 * Sidebar and the MobileNav drawer. Any admin route selects the admin item.
 */
export const useMenuSelectedKeys = (t: TFunction<"dashboard">, user?: IUser): string[] => {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (pathname.includes("/admin")) {
      return [ROUTES_MAP.dashboard.admin.liveStreams, ROUTES_MAP.dashboard.admin.users];
    }
    const currentItem = buildMenuItems(t, user).find((item) => pathname.startsWith(item.key));
    return currentItem ? [currentItem.key] : [];
  }, [pathname, user, t]);
};
